import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import {
  createSpecificationItem,
  getSpecificationItemById,
  updateSpecificationItem,
} from "../../api/finishedGoods";
import { getProductCategoryList } from "../../api/products";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("El nombre es obligatorio"),
  quantity: Yup.number()
    .min(1, "La cantidad debe ser mayor a 0")
    .required("La cantidad es obligatoria"),
  productCategory: Yup.object()
    .nullable()
    .required("El material principal es obligatorio"),
  productCategoryId: Yup.number().nullable(),
  description: Yup.string(),
  props: Yup.string(),
});

const initialValues = {
  name: "",
  quantity: 1,
  productCategoryId: null,
  productCategory: null,
  description: "",
  props: "",
};

const SpecificationItemForm = React.memo(function SpecificationItemForm({ finishedGoodId, id }) {
  const [formData, setFormData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { openSnack } = useSnack();

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormData(initialValues);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSpecificationItemById(id);
      const data = res.data;
      
      // If we have productCategoryId, we need to find the corresponding category object
      let productCategory = null;
      if (data.productCategoryId) {
        try {
          const categoriesRes = await getProductCategoryList({ code: null });
          const categories = categoriesRes.data || [];
          productCategory = categories.find(cat => cat.id === data.productCategoryId) || null;
        } catch (e) {
          console.warn("Could not fetch product categories for existing item:", e);
        }
      }
      
      setFormData({
        ...data,
        productCategory
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  }, [id, openSnack]);

  const handleSubmit = useCallback(async (values) => {
    if (submitting) return; // Prevent double submission
    
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        finishedGoodId,
        quantity: parseInt(values.quantity),
        productCategoryId: values.productCategory?.id ? parseInt(values.productCategory.id) : null,
      };

      if (id) {
        await updateSpecificationItem(id, payload);
        openSnack("Especificación actualizada con éxito", "success");
      } else {
        await createSpecificationItem(payload);
        openSnack("Especificación creada con éxito", "success");
      }
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setSubmitting(false);
    }
  }, [id, openSnack, submitting]);

  // Memoized callback for handling product category selection
  const handleCategoryChange = useCallback((event, newValue, setFieldValue) => {
    setFieldValue("productCategory", newValue);
    setFieldValue("productCategoryId", newValue?.id || null);
  }, []);

  if (loading) {
    return <Typography>Cargando...</Typography>;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={true}
    >
      {({ values, handleChange, handleBlur, touched, errors, setFieldValue }) => (
        <Form>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 400,
              p: 2,
            }}
          >
            <TextField
              fullWidth
              label="Nombre"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              required
            />

            <TextField
              fullWidth
              label="Cantidad"
              name="quantity"
              type="number"
              value={values.quantity}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.quantity && Boolean(errors.quantity)}
              helperText={touched.quantity && errors.quantity}
              required
              inputProps={{ min: 1 }}
            />

            <LGAutocomplete
              fetchFunction={getProductCategoryList}
              optionLabel="name"
              fetchBy="name"
              value={values.productCategory}
              onChange={(event, newValue) => {
                handleCategoryChange(event, newValue, setFieldValue);
              }}
              label="Material principal"
              error={touched.productCategory && Boolean(errors.productCategory)}
              helperText={touched.productCategory && errors.productCategory}
              required
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              multiline
              rows={3}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />

            <TextField
              fullWidth
              label="Propiedades adicionales"
              name="props"
              multiline
              rows={2}
              value={values.props}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.props && Boolean(errors.props)}
              helperText={touched.props && errors.props}
              placeholder="Información adicional sobre las especificaciones"
            />

            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={submitting}
            >
              {submitting ? "Procesando..." : (id ? "Actualizar" : "Crear")}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
});

export default SpecificationItemForm;